import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, CalendarDays, Sparkles, Briefcase, GraduationCap, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import HowItWorks from "@/components/HowItWorks";
import BrandMark from "@/components/BrandMark";
import Seo from "@/components/Seo";

const LANDING_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "VaidGram — Private network for BAMS & Ayurveda scholars",
  "url": "https://vaidgram.in/",
  "about": {
    "@type": "Thing",
    "name": "Ayurveda scholar community, BAMS network, Ayurveda jobs, case discussions, meet-ups"
  },
  "mainEntity": {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is VaidGram?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "VaidGram is a private social network exclusively for BAMS, MD (Ayurveda), MS (Ayurveda) and PhD Ayurveda scholars in India. Members reconnect with their college batch, host case-discussion meet-ups, share peer-reviewed posts and browse verified Ayurveda jobs."
        }
      },
      {
        "@type": "Question",
        "name": "Who can join VaidGram?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Any Ayurveda scholar who has completed or is pursuing BAMS, MD (Ayurveda), MS (Ayurveda) or PhD from a recognised Indian college. Every sign-up captures BAMS college, university and batch year."
        }
      },
      {
        "@type": "Question",
        "name": "Are there Ayurveda jobs on VaidGram?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. VaidGram has a dedicated BAMS / MD Ayurveda jobs board — clinical, teaching, research and Panchakarma roles across India, posted by hospitals, colleges and senior Vaidyas."
        }
      },
      {
        "@type": "Question",
        "name": "Which Ayurveda specialties are supported?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kayachikitsa, Panchakarma, Shalya Tantra, Shalakya Tantra, Prasuti Tantra & Stree Roga, Kaumarbhritya, Agada Tantra, Rasashastra, Dravyaguna, Rog Nidan, Samhita & Siddhanta, Swasthavritta, Kriya Sharir and Rachana Sharir."
        }
      }
    ]
  }
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/app", { replace: true }); }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)] text-[color:var(--ayur-ink)]">
      <Seo
        title="Private network for BAMS, MD & MS Ayurveda scholars"
        description="VaidGram is India's private social network for Ayurveda scholars — BAMS, MD, MS, PhD. Reconnect with your college batch, discuss cases, join meet-ups and find verified Ayurveda jobs."
        path="/"
        keywords="Ayurveda, BAMS, BAMS community, BAMS jobs, MD Ayurveda, MS Ayurveda, Vaidya, Vaid, VaidGram, Vaid Gram, ayurveda scholars, ayurveda network, ayurveda meetups, ayurveda case discussion, ayurveda journal club, BAMS alumni, Panchakarma, Kayachikitsa, Shalya Tantra, Dravyaguna, Rasashastra, Kaumarbhritya, Prasuti Tantra, Swasthavritta"
        jsonLd={LANDING_JSONLD}
      />
      <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center text-[color:var(--ayur-emerald)]">
            <BrandMark size={56} />
          </span>
          <span className="font-serif-display text-2xl font-semibold text-[color:var(--ayur-emerald)]">VaidGram</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login-link" className="text-sm font-medium text-[color:var(--ayur-ink-2)] link-underline">Log in</Link>
          <Button data-testid="landing-signup-cta" onClick={() => navigate("/signup")}
            className="bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white rounded-full px-5">
            Join the Samvad
          </Button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="hero-noise absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-16 items-center relative">
          <div>
            <div className="label-eyebrow mb-4">A private guild for Ayurveda</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight font-semibold text-[color:var(--ayur-emerald)]">
              Our personal
              <span className="italic text-[color:var(--ayur-terracotta)]"> webspace.</span>
            </h1>
            <p className="mt-6 text-lg text-[color:var(--ayur-ink-2)] max-w-lg leading-relaxed">
              Find and reconnect with old classmates, make new friends, and enjoy
              endless conversations with like-minded Ayurveda scholars — for us, only us.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-primary-cta" onClick={() => navigate("/signup")}
                className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white rounded-full px-7 py-6 text-base">
                Create your scholar profile
              </Button>
              <Button variant="ghost" onClick={() => navigate("/login")}
                className="rounded-full px-6 py-6 text-base text-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-cream-2)]">
                I already have an account
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-3 text-sm text-[color:var(--ayur-mute)]">
              <span className="pill">BAMS</span>
              <span className="pill">MD Ayurveda</span>
              <span className="pill">MS Ayurveda</span>
              <span className="pill">PhD</span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-[color:var(--ayur-emerald)]/10 blur-2xl" />
            <div className="relative paper-card p-2 overflow-hidden">
              <img
                src="/hero.png"
                alt="Confident group of Indian BAMS Ayurveda students at the VaidGram campus"
                className="w-full h-[480px] object-cover rounded-lg"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 paper-card p-4 max-w-xs">
              <div className="label-eyebrow">Case discussion</div>
              <div className="font-serif-display text-lg mt-1 text-[color:var(--ayur-ink)]">
                “Managing IBS with a modern Panchakarma protocol — your take?”
              </div>
              <div className="text-xs text-[color:var(--ayur-mute)] mt-2">Dr. Ananya · Panchakarma · BHU</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pt-4" data-testid="landing-college-directory-box">
        <Link to="/ayurveda-colleges" data-testid="landing-college-directory-cta"
          className="group block rounded-2xl bg-[color:var(--ayur-emerald)] text-white p-8 sm:p-10 ring-4 ring-[color:var(--ayur-gold)] shadow-xl hover:-translate-y-0.5 transition-transform duration-200">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="label-eyebrow text-[color:var(--ayur-gold)]">New · Admissions 2026-27</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl mt-2">Ayurveda College Directory</h2>
              <p className="mt-3 text-white/85 max-w-2xl text-sm md:text-base">All 497 NCISM-permitted BAMS, MD & MS Ayurveda colleges across 26 states, with seats, PG subjects, Govt/Private status and full address. Filter by state, course and specialty.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[color:var(--ayur-gold)] text-[color:var(--ayur-ink)] px-6 py-3 font-semibold group-hover:bg-white transition-colors">
              <GraduationCap className="w-5 h-5" /> Explore colleges
            </span>
          </div>
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
        {[
          { icon: Users, title: "Verified BAMS peers only", body: "Every scholar signs up with their BAMS college, university and batch year — no anonymous noise, only the guild." },
          { icon: CalendarDays, title: "Meet-ups & case discussions", body: "Host online case discussions, Panchakarma journal clubs, CME talks and specialty meet-ups. RSVP with one tap." },
          { icon: Sparkles, title: "Specialty-native feed", body: "Filter posts by Kayachikitsa, Panchakarma, Shalya, Dravyaguna, Rasashastra, Prasuti, Kaumarbhritya and more." },
          { icon: Briefcase, title: "Ayurveda jobs board", body: "Verified BAMS / MD (Ayurveda) / MS (Ayurveda) jobs across India — clinical, teaching, research and Panchakarma roles." },
          { icon: GraduationCap, title: "Alumni & Gurus", body: "Reconnect with your BAMS college batch and follow senior Vaidyas across every classical specialty." },
          { icon: BookOpen, title: "Samhita-first conversations", body: "Discussions rooted in Charaka, Sushruta and Ashtanga Hridaya — modern practice, classical foundation." },
        ].map((f, i) => (
          <div key={i} className="paper-card p-7">
            <f.icon className="w-6 h-6 text-[color:var(--ayur-terracotta)]" />
            <h3 className="font-serif-display text-xl font-semibold mt-4">{f.title}</h3>
            <p className="text-sm text-[color:var(--ayur-ink-2)] mt-2 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      {/* SEO-rich content — Ayurveda specialties + colleges */}
      <section className="max-w-7xl mx-auto px-6 pb-16" aria-labelledby="specialties-heading">
        <div className="paper-card p-8 sm:p-10">
          <h2 id="specialties-heading" className="font-serif-display text-3xl text-[color:var(--ayur-emerald)]">
            Every Ayurveda specialty — one guild
          </h2>
          <p className="mt-3 text-[color:var(--ayur-ink-2)] max-w-3xl leading-relaxed">
            VaidGram brings together BAMS scholars and Ayurveda practitioners from across India. Whatever you specialise in, you'll find peers and gurus in your stream:
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              "Kayachikitsa (Internal Medicine)",
              "Panchakarma (Detox & Rejuvenation)",
              "Shalya Tantra (Surgery)",
              "Shalakya Tantra (ENT & Ophthalmology)",
              "Prasuti Tantra & Stree Roga (Women's Health)",
              "Kaumarbhritya (Paediatrics)",
              "Agada Tantra (Toxicology)",
              "Rasashastra & Bhaishajya Kalpana (Pharmaceutics)",
              "Dravyaguna (Materia Medica)",
              "Rog Nidan (Diagnosis)",
              "Samhita & Siddhanta (Classical Texts)",
              "Swasthavritta (Preventive Medicine)",
              "Kriya Sharir (Physiology)",
              "Rachana Sharir (Anatomy)",
              "Kaumarbhritya",
              "Yoga & Naturopathy adjuncts",
            ].map((s) => (
              <span key={s} className="pill text-xs text-[color:var(--ayur-emerald)] bg-[color:var(--ayur-cream-2)] block truncate">
                {s}
              </span>
            ))}
          </div>

          <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] mt-10">
            Reconnect with your BAMS college & batch
          </h2>
          <p className="mt-3 text-[color:var(--ayur-ink-2)] max-w-3xl leading-relaxed">
            Scholars from India's leading Ayurveda colleges are already on VaidGram — Institute of Medical Sciences BHU (Varanasi), IPGT&amp;RA Jamnagar, National Institute of Ayurveda Jaipur, All India Institute of Ayurveda Delhi, Kerala Ayurveda Academy, Tilak Ayurved Vidyapeeth Pune, Gujarat Ayurved University, RGUHS Bengaluru, Kottakkal Arya Vaidya Sala, Government Ayurveda College Trivandrum, and many more. Sign up with your college name and batch year and your batch group will surface automatically.
          </p>

          <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] mt-10">
            Verified Ayurveda jobs — clinical, teaching, research
          </h2>
          <p className="mt-3 text-[color:var(--ayur-ink-2)] max-w-3xl leading-relaxed">
            Hiring a Panchakarma physician, an MS Shalya consultant, or a research fellow in Rasashastra? Post an Ayurveda job on VaidGram and reach a curated network of BAMS, MD (Ayurveda), MS (Ayurveda) and PhD scholars. Sponsored listings pin your role to the top of every scholar's job board.
          </p>

          <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] mt-10">
            Frequently asked questions
          </h2>
          <div className="mt-4 space-y-4 text-sm text-[color:var(--ayur-ink-2)] max-w-3xl leading-relaxed">
            <details className="paper-card p-4 bg-[color:var(--ayur-cream-2)]">
              <summary className="cursor-pointer font-semibold text-[color:var(--ayur-ink)]">What does VaidGram mean?</summary>
              <p className="mt-2">"Vaid" (or Vaidya) is the Sanskrit word for a healer trained in Ayurveda; "Gram" means village. VaidGram is a digital village for BAMS, MD and MS Ayurveda scholars.</p>
            </details>
            <details className="paper-card p-4 bg-[color:var(--ayur-cream-2)]">
              <summary className="cursor-pointer font-semibold text-[color:var(--ayur-ink)]">Is VaidGram free?</summary>
              <p className="mt-2">Yes — creating a scholar profile, following peers, joining meet-ups and browsing Ayurveda jobs is free. Some job listings are sponsored (paid) to pin them to the top.</p>
            </details>
            <details className="paper-card p-4 bg-[color:var(--ayur-cream-2)]">
              <summary className="cursor-pointer font-semibold text-[color:var(--ayur-ink)]">Do I have to be a doctor to join?</summary>
              <p className="mt-2">VaidGram is for Ayurveda scholars — anyone who has completed or is pursuing BAMS, MD (Ayurveda), MS (Ayurveda) or PhD Ayurveda from a recognised Indian institution.</p>
            </details>
            <details className="paper-card p-4 bg-[color:var(--ayur-cream-2)]">
              <summary className="cursor-pointer font-semibold text-[color:var(--ayur-ink)]">Can I discuss a specific case with peers?</summary>
              <p className="mt-2">Absolutely. Create a meet-up with reason "Case discussion", pick your specialty (e.g. Kayachikitsa, Panchakarma), and invite peers. A private discussion group opens 30 minutes before start and closes at the end time you choose.</p>
            </details>
          </div>
        </div>
      </section>

      <HowItWorks />
    </div>
  );
}
